import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { EmailCombobox } from '../crm/EmailCombobox';
import type { RequestKind, RequestStatus } from './api';
import {
  useCreateDataSubjectRequest,
  useDataSubjectRequests,
  useUpdateDataSubjectRequest,
} from './queries';
import { Select } from '../../components/Select';

const KINDS: RequestKind[] = [
  'access',
  'rectification',
  'erasure',
  'portability',
  'objection',
  'restriction',
];

const STATUSES: RequestStatus[] = ['open', 'in_progress', 'completed', 'rejected'];

/**
 * Die Betroffenenersuchen -- F5-11.
 *
 * **Die Frist ist die Hauptsache.** Art. 12 Abs. 3 DSGVO gibt einen Monat ab
 * Eingang; die Liste steht deshalb nach Faelligkeit und nicht nach Eingang,
 * und was ablaeuft, ist rot. Ein Ersuchen, das man erst sieht, wenn man
 * danach sucht, ist ein Ersuchen, dessen Frist verstreicht.
 *
 * **Ein Ersuchen darf ohne zugeordnete Person entstehen.** Es trifft per Mail
 * ein, und wer dahintersteckt, ist nicht immer sofort klar -- die Frist laeuft
 * trotzdem. Deshalb genuegt hier die Adresse.
 */
export function RequestsPanel() {
  const { t } = useTranslation();
  const [openOnly, setOpenOnly] = useState(true);
  const requests = useDataSubjectRequests(openOnly);
  const create = useCreateDataSubjectRequest();
  const update = useUpdateDataSubjectRequest();

  const [kind, setKind] = useState<RequestKind>('access');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');

  const now = Date.now();

  return (
    <div className="stack">
      <section className="card">
        <label className="field__checkbox">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(event) => {
              setOpenOnly(event.target.checked);
            }}
          />
          {t('privacy.openOnly')}
        </label>
      </section>

      <section className="card card--flush">
        {requests.isPending ? <SkeletonTable columns={5} /> : null}
        {requests.isError ? <ErrorNotice error={requests.error} /> : null}

        {requests.data?.length === 0 ? (
          <EmptyState
            icon="privacy"
            title={t('privacy.noRequests')}
            hint={t('privacy.noRequestsHint')}
          />
        ) : null}

        {requests.data && requests.data.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('privacy.kind')}</th>
                  <th scope="col">{t('privacy.subject')}</th>
                  <th scope="col">{t('privacy.receivedAt')}</th>
                  <th scope="col">{t('privacy.dueAt')}</th>
                  <th scope="col">{t('privacy.status')}</th>
                </tr>
              </thead>
              <tbody>
                {requests.data.map((entry) => {
                  const overdue =
                    entry.completed_at === null && new Date(entry.due_at).getTime() < now;
                  return (
                    <tr key={entry.id}>
                      <td>{t(`requestKind.${entry.kind}`)}</td>
                      <td className="table__muted">{entry.subject_email ?? '—'}</td>
                      <td className="table__muted">
                        {new Date(entry.received_at).toLocaleDateString()}
                      </td>
                      <td>
                        {new Date(entry.due_at).toLocaleDateString()}{' '}
                        {overdue ? <Badge tone="danger">{t('privacy.overdue')}</Badge> : null}
                      </td>
                      <td>
                        <Select
                          className="field__control"
                          aria-label={t('privacy.status')}
                          value={entry.status}
                          onChange={(event) => {
                            update.mutate({
                              requestId: entry.id,
                              payload: { status: event.target.value },
                            });
                          }}
                        >
                          {STATUSES.map((value) => (
                            <option key={value} value={value}>
                              {t(`requestStatus.${value}`)}
                            </option>
                          ))}
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {update.isError ? <ErrorNotice error={update.error} /> : null}

      <section className="card">
        <h2>{t('privacy.newRequest')}</h2>
        <p className="table__muted">{t('privacy.newRequestHint')}</p>

        <div className="field">
          <label className="field__label" htmlFor="request-kind">
            {t('privacy.kind')}
          </label>
          <Select
            id="request-kind"
            className="field__control"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as RequestKind);
            }}
          >
            {KINDS.map((value) => (
              <option key={value} value={value}>
                {t(`requestKind.${value}`)}
              </option>
            ))}
          </Select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="request-email">
            {t('privacy.subject')}
          </label>
          {/* Vorgeschlagen werden Ansprechpartner aus dem CRM; eine Adresse,
              die dort nicht steht, bleibt zulaessig -- Betroffene sind nicht
              immer Kontakte. */}
          <EmailCombobox id="request-email" value={email} onChange={setEmail} />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="request-note">
            {t('privacy.note')}
          </label>
          <textarea
            id="request-note"
            className="field__control"
            rows={3}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </div>

        {create.isError ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={!email.trim()}
          busy={create.isPending}
          onClick={() => {
            create.mutate(
              { kind, subject_email: email.trim(), note },
              {
                onSuccess: () => {
                  setEmail('');
                  setNote('');
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>
    </div>
  );
}
